import React, { useEffect, useState } from "react";
import { Box, Form, FormField, TextInput, Text, Anchor } from "grommet";
import {signIn} from "next-auth/react";
import NarrowHeading from "../components/NarrowHeading";
import StatusMessage from "../components/StatusMessage";
import LoginPageLayout from "../components/layouts/LoginPageLayout";
import Loading from "../components/Loading";
import { debounce } from "../utils/debounce";
import { handleErrors } from "../utils/errors";
import { useRouter } from "next/router";
import Button from "./Button";
import MessageBanner from "./MessageBanner";
import { IconInfoCircle as IconInfo } from '@tabler/icons-react';

const Confirm = ({email, password}) => {
    const router = useRouter();
    const [formValues, setFormValues] = useState({});
    const [formError, setFormError] = useState();
    const [isLoading, setIsLoading] = useState();
    const [statusMessage, setStatusMessage] = useState();
    const [rateLimit, setRateLimit] = useState(0);
    const maxAttempts = 20;

    const isProduction = process.env.NODE_ENV === 'production' && process.env.NEXT_PUBLIC_VERCEL_ENV !== 'preview';
    const showTestBanner = !isProduction;

    const onSubmit = (e) => {
        setRateLimit(rateLimit + 1);
        if (rateLimit >= maxAttempts) {
            return;
        }
        setIsLoading(true);
        setStatusMessage();
        fetch(`/api/auth/confirm?confirmation_token=${e?.value?.confirmation_token}&email=${encodeURIComponent(email)}`)
            .then(handleErrors)
            .then((data) => {
                if (data) {
                    setFormError();
                    const credentials = { email, password };
                    const redirectTo = "/signup/business";
                    const postData = { ...credentials, redirect: false };
                    signIn("credentials", postData)
                        .then(async (data) => {
                            if (data.error) {
                                setIsLoading();
                                return;
                            }
                            if (data.status === 200) {
                                return router.push(redirectTo);
                            }
                        })
                        .catch((error) => {
                            setIsLoading(false);
                        });
                }
            })
            .catch((error) => {
                setFormError(error?.message);
                setIsLoading();
            })
    };

    const requestVerification = () => {
        setFormError();
        fetch(`/api/auth/confirm`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ email }),
        })
            .then(handleErrors)
            .then((data) => {
                if (data) {
                    setStatusMessage("Verification code resent.");
                }
            })
            .catch((error) => {
                setFormError(error?.message);
            })
            .finally(() => {
                setIsLoading();
            });
    };

    useEffect(() => {
        if (rateLimit >= maxAttempts) {
            setFormError("Sorry, that's the wrong code. Please retry in 24 hours.")
        }
    }, [rateLimit]);

    const useTestCode = () => {
        setFormValues({ confirmation_token: '000000' });
        onSubmit({ value: { confirmation_token: '000000' } });
    };

    return (
        <LoginPageLayout title="Verify your email">
            <Box>
                <NarrowHeading
                    heading="Verify your email"
                    paragraph={
                        <Text>
                           We just sent a 6 digit verification code to <b>{email}</b>. Please check your email and enter the verification code below.
                        </Text>
                    }
                />
                {showTestBanner && (
                    <MessageBanner
                        icon={IconInfo}
                        color="status-warning"
                        message="Skip this with a test verification code."
                        ctaText="Use test code"
                        onClick={useTestCode}
                        containerProps={{ margin: { bottom: 'small' } }}
                    />
                )}
                <Form
                    value={formValues}
                    onSubmit={debounce(onSubmit, 300)}
                    onChange={setFormValues}
                    messages={{ required: "Confirmation code is required" }}
                >
                    {statusMessage && <StatusMessage message={statusMessage} type="ok" />}
                    <FormField
                        placeholder="000000"
                        name="confirmation_token"
                        autocomplete="off"
                        component={TextInput}
                        error={formError}
                        required
                    />
                    <Box pad={{ vertical: "medium", horizontal: "none" }}>
                        <Button
                            event={{section: 'signup_phone_verification_form', element: 'submit'}}
                            type="submit"
                            primary
                            label={isLoading ? <Loading plain color="magento" /> : "Submit"}
                            disabled={isLoading || rateLimit >= maxAttempts}
                        />
                        <Box pad={{top: 'xsmall'}} margin={{top: "xsmall"}}align="center">
                            <Text size="xsmall" color="text-weak">Didn't receive the code? <Anchor onClick={requestVerification}>Resend email</Anchor></Text>
                        </Box>
                    </Box>
                </Form>
            </Box>
        </LoginPageLayout>
    );
};

export default Confirm;

Confirm.auth = false;
